import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { AdminShell, GhostButton, PrimaryButton, StatusChip, SourceTag, AssessmentTypeChip, DangerButton, TextField } from './ui.tsx';
import { AnalysisContent } from './AnalysisDashboard.tsx';
import { RespondentsView } from './RespondentsView.tsx';
import { SurveyConfigView } from './SurveyConfigView.tsx';
import { adminApi, ApiError } from './admin-api.ts';
import type { AdminUser, EngagementListItem } from './types.ts';

type Tab = 'analysis' | 'respondents' | 'configure';

export function EngagementDetail({
  engagement,
  admin,
  onBack,
  onLogout,
}: {
  engagement: EngagementListItem;
  admin: AdminUser;
  onBack: () => void;
  onLogout: () => void;
}) {
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tab>('respondents');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  // Local override so the heading updates immediately after a save — `engagement` itself
  // is a snapshot handed down from the list and isn't refetched while this screen is open.
  const [clientName, setClientName] = useState(engagement.clientName);
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState(engagement.clientName);
  const [savingName, setSavingName] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);

  const saveClientName = async () => {
    const trimmed = nameDraft.trim();
    if (!trimmed || trimmed === clientName) {
      setEditingName(false);
      setNameDraft(clientName);
      return;
    }
    setSavingName(true);
    setNameError(null);
    try {
      await adminApi.updateEngagement(engagement.id, { clientName: trimmed });
      setClientName(trimmed);
      setEditingName(false);
      void qc.invalidateQueries({ queryKey: ['admin-engagements'] });
    } catch (e) {
      setNameError(e instanceof ApiError ? e.message : 'Could not save');
    } finally {
      setSavingName(false);
    }
  };

  const tabs: { key: Tab; label: string }[] = [
    { key: 'respondents', label: 'Respondents' },
    { key: 'analysis', label: 'Analysis' },
    { key: 'configure', label: 'Configure Survey' },
  ];

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await adminApi.deleteEngagement(engagement.id);
      onBack();
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    setExportError(null);
    try {
      await adminApi.downloadExport(engagement.id);
    } catch (e) {
      setExportError(e instanceof ApiError ? e.message : 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  return (
    <AdminShell admin={admin} onLogout={onLogout}>
      <div className="mb-6">
        <GhostButton onClick={onBack}>← Engagements</GhostButton>
        <div className="mt-3 flex flex-wrap items-start justify-between gap-3">
          <div>
            {editingName ? (
              <div className="flex items-start gap-2">
                <TextField id="client-name-edit" label="Client name" value={nameDraft} onChange={setNameDraft} />
                <PrimaryButton onClick={() => void saveClientName()} disabled={savingName}>
                  {savingName ? 'Saving…' : 'Save'}
                </PrimaryButton>
                <GhostButton
                  onClick={() => {
                    setEditingName(false);
                    setNameDraft(clientName);
                    setNameError(null);
                  }}
                >
                  Cancel
                </GhostButton>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-bold text-ink">{clientName}</h1>
                <button
                  type="button"
                  onClick={() => setEditingName(true)}
                  className="text-xs font-medium text-ink-soft underline-offset-4 hover:underline"
                >
                  Edit
                </button>
              </div>
            )}
            {nameError && <p className="mt-1 text-xs text-red-600">{nameError}</p>}
            <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-ink-soft">
              <span>{engagement.internalName}</span>
              <StatusChip status={engagement.status} />
              <SourceTag source={engagement.source} />
              <AssessmentTypeChip assessmentType={engagement.assessmentType} />
            </div>
          </div>
          <div className="flex flex-col items-end gap-1">
            <PrimaryButton onClick={handleExport} disabled={exporting}>
              {exporting ? 'Exporting…' : 'Export to Excel'}
            </PrimaryButton>
            {exportError && <span className="text-xs text-red-600">{exportError}</span>}
          </div>
        </div>
        <div className="mt-4 flex items-center justify-between gap-1 border-b border-black/10">
          <div className="flex gap-1">
            {tabs.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium transition ${
                  tab === t.key ? 'border-[var(--accent)] text-ink' : 'border-transparent text-ink-soft hover:text-ink'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <DangerButton onClick={() => setShowDeleteConfirm(true)}>Delete</DangerButton>
        </div>
      </div>

      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="mx-4 w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl">
            <h2 className="text-lg font-semibold text-ink">Delete engagement?</h2>
            <p className="mt-2 text-sm text-ink-soft">
              This will permanently delete <strong>{clientName} — {engagement.internalName}</strong> and all its respondents, responses, and analysis data. This cannot be undone.
            </p>
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(false)}
                className="rounded-lg px-4 py-2 text-sm font-medium text-ink-soft hover:bg-black/5"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleting}
                className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50"
              >
                {deleting ? 'Deleting…' : 'Yes, delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {tab === 'analysis' ? (
        <AnalysisContent engagementId={engagement.id} />
      ) : tab === 'configure' ? (
        <SurveyConfigView engagementId={engagement.id} />
      ) : (
        <RespondentsView engagementId={engagement.id} />
      )}
    </AdminShell>
  );
}
